import { ChevronDown, ChevronUp, Plus, Table2, Trash2, WandSparkles } from "lucide-react";
import { useState } from "react";

import {
  parsePastedTable,
  type ManuscriptTable,
} from "../domain/manuscript";

type TablesEditorProps = {
  tables: ManuscriptTable[];
  onAdd: (rows: string[][]) => void;
  onChange: (
    id: string,
    patch: Partial<Omit<ManuscriptTable, "id">>,
  ) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, direction: -1 | 1) => void;
};

function updateCell(
  rows: string[][],
  rowIndex: number,
  columnIndex: number,
  value: string,
): string[][] {
  return rows.map((row, currentRow) =>
    currentRow === rowIndex
      ? row.map((cell, currentColumn) =>
          currentColumn === columnIndex ? value : cell,
        )
      : row,
  );
}

export function TablesEditor({
  tables,
  onAdd,
  onChange,
  onDelete,
  onMove,
}: TablesEditorProps) {
  const [pasteValue, setPasteValue] = useState("");
  const [pasteError, setPasteError] = useState("");

  const convertPaste = () => {
    const rows = parsePastedTable(pasteValue);
    if (!rows.length) {
      setPasteError("没有识别到至少 2 列的数据。请从 Word、Excel 或表格网页直接复制后再粘贴。");
      return;
    }
    onAdd(rows);
    setPasteValue("");
    setPasteError("");
  };

  return (
    <main className="editor-panel">
      <div className="editor-hero">
        <div className="hero-icon">
          <Table2 size={22} />
        </div>
        <div>
          <span className="eyebrow">THREE-LINE TABLES</span>
          <h1>粘贴并转换三线表</h1>
          <p>从 Word、Excel 或网页表格复制后粘贴；第一行作为表头，生成可编辑的标准三线表。</p>
        </div>
      </div>

      <section className="editor-card table-paste-card">
        <label>
          <span className="field-label">粘贴表格数据</span>
          <textarea
            aria-label="粘贴表格数据"
            onChange={(event) => setPasteValue(event.target.value)}
            placeholder={"指标\t处理组\t对照组\n均值\t12.4\t10.1\nP 值\t0.021\t—"}
            value={pasteValue}
          />
        </label>
        <div className="table-paste-actions">
          <button disabled={!pasteValue.trim()} onClick={convertPaste} type="button">
            <WandSparkles size={15} /> 识别并转为三线表
          </button>
          <span>支持制表符、CSV 和 Markdown 表格；最多 100 行、30 列。</span>
        </div>
        {pasteError ? <p className="table-paste-error">{pasteError}</p> : null}
      </section>

      {tables.map((table, tableIndex) => {
        const columnCount = table.rows[0]?.length ?? 0;
        return (
          <section className="editor-card editable-table-card" key={table.id}>
            <header>
              <div>
                <span className="table-number">Table {tableIndex + 1}</span>
                <input
                  aria-label={`Table ${tableIndex + 1} 标题`}
                  onChange={(event) =>
                    onChange(table.id, { title: event.target.value })
                  }
                  placeholder="填写表题（不需要重复输入 Table 1）"
                  value={table.title}
                />
              </div>
              <div className="table-card-actions">
                <button
                  aria-label={`上移 Table ${tableIndex + 1}`}
                  disabled={tableIndex === 0}
                  onClick={() => onMove(table.id, -1)}
                  type="button"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  aria-label={`下移 Table ${tableIndex + 1}`}
                  disabled={tableIndex === tables.length - 1}
                  onClick={() => onMove(table.id, 1)}
                  type="button"
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  aria-label={`删除 Table ${tableIndex + 1}`}
                  className="danger-icon"
                  onClick={() => onDelete(table.id)}
                  type="button"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </header>

            <div className="editable-three-line-scroll">
              <table className="editable-three-line-table">
                <tbody>
                  {table.rows.map((row, rowIndex) => (
                    <tr key={`${table.id}-row-${rowIndex}`}>
                      {row.map((cell, columnIndex) => (
                        <td key={`${table.id}-${rowIndex}-${columnIndex}`}>
                          <input
                            aria-label={`Table ${tableIndex + 1} 第 ${rowIndex + 1} 行第 ${columnIndex + 1} 列`}
                            onChange={(event) =>
                              onChange(table.id, {
                                rows: updateCell(
                                  table.rows,
                                  rowIndex,
                                  columnIndex,
                                  event.target.value,
                                ),
                              })
                            }
                            value={cell}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="editable-table-tools">
              <button
                onClick={() =>
                  onChange(table.id, {
                    rows: [
                      ...table.rows,
                      Array.from({ length: Math.max(columnCount, 2) }, () => ""),
                    ],
                  })
                }
                type="button"
              >
                <Plus size={13} /> 添加行
              </button>
              <button
                onClick={() =>
                  onChange(table.id, {
                    rows: table.rows.map((row) => [...row, ""]),
                  })
                }
                type="button"
              >
                <Plus size={13} /> 添加列
              </button>
              <button
                disabled={table.rows.length <= 2}
                onClick={() =>
                  onChange(table.id, { rows: table.rows.slice(0, -1) })
                }
                type="button"
              >
                删除末行
              </button>
              <button
                disabled={columnCount <= 2}
                onClick={() =>
                  onChange(table.id, {
                    rows: table.rows.map((row) => row.slice(0, -1)),
                  })
                }
                type="button"
              >
                删除末列
              </button>
            </div>

            <label className="table-notes-field">
              <span className="field-label">表下注释（可选）</span>
              <textarea
                aria-label={`Table ${tableIndex + 1} 表下注释`}
                onChange={(event) =>
                  onChange(table.id, { notes: event.target.value })
                }
                placeholder="缩写、统计检验或数据说明；导出时自动添加 Note: 标签。"
                rows={2}
                value={table.notes}
              />
            </label>
          </section>
        );
      })}
    </main>
  );
}
